import React, { useState, useMemo, useEffect } from 'react';
import CheckIcon from './icons/CheckIcon';
import SparklesIcon from './icons/SparklesIcon';
import CreditCardIcon from './icons/CreditCardIcon';
import PayPalIcon from './icons/PayPalIcon';
import GooglePayIcon from './icons/GooglePayIcon';
import ApplePayIcon from './icons/ApplePayIcon';
import ChatBubbleIcon from './icons/ChatBubbleIcon';
import AmplifierIcon from './icons/AmplifierIcon';
import PhotoIcon from './icons/PhotoIcon';
import BriefcaseIcon from './icons/BriefcaseIcon'; 
import { useUI } from '../contexts/UIProvider';
import { useAuth } from '../contexts/AuthProvider';
import type { UserRole, UserCluster } from '../types';

// Robust cluster detection for subscription tier prefixes
const getClusterPrefix = (roles?: UserRole[]): UserCluster => {
    if (!roles || roles.length === 0) return 'talent';
    
    const clusterMap: Record<string, UserRole[]> = {
        talent: ['Musician', 'Singer', 'DJ', 'Actor', 'Director', 'Dancer', 'Performer', 'Choreographer', 'Model', 'Comedian'],
        business: ['Venue', 'Live Club', 'Theatre', 'Cinema', 'Festival', 'Label', 'Radio', 'Movie/TV Production', 'Casting Agency', 'Event Agency', 'Publisher', 'Producer'],
        service: ['Audio Service', 'Recording Studio', 'Music Store', 'Luthier', 'Sound Engineer', 'Light Designer', 'Filmmaker / Videographer', 'Photographer', 'Make-Up Artist (MUA)', 'Costume Designer / Stylist', 'Backline Rental', 'Graphic Designer'],
        edu: ['Music School', 'Acting Academy', 'Dance School', 'Cultural Association', 'Vocal Coach'],
        fan: ['Music Enthusiast', 'Music Addict', 'Live Music Fan', 'Concert Goer']
    };

    const activeClusters = new Set<UserCluster>();
    roles.forEach(role => {
        for (const [cluster, roleList] of Object.entries(clusterMap)) {
            if (roleList.includes(role)) activeClusters.add(cluster as UserCluster);
        }
    });

    if (activeClusters.has('fan') && activeClusters.size === 1) return 'fan';
    activeClusters.delete('fan');
    if (activeClusters.size > 1) return 'multi';
    return Array.from(activeClusters)[0] || 'talent';
};

const features: Record<string, (string | React.ReactNode)[]> = {
    Basic: [
        "Professional Profile", 
        <span key="f1">Post 1 opportunity / month</span>, 
        <span key="f2">Apply to 1 opportunity / month</span>, 
        "3 Chats / month (diff. users)",
        "Get Endorsed", 
        "Pay-As-You-Go Options"
    ],
    Pro: ["All Basic features", "Unlimited Posts", "Unlimited Applications", "Unlimited Direct Messages", "Calendar Management", "Lineups & Flyers", "Remove Ads", "Pro Badge"],
    ProPlus: ["All Pro features", "AI Assistant (Drafting)", "Team Management", "Stealth (Scout) Mode", "Priority Support", "Advanced Analytics"]
};

const PumpUpTheVolumePage: React.FC = () => {
    const { showToast } = useUI();
    const { currentUser } = useAuth();
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

    const clusterPrefix = useMemo(() => {
        const roles = currentUser?.roles || (currentUser?.role ? [currentUser.role] : []);
        return getClusterPrefix(roles);
    }, [currentUser]);

    const handleSubscribe = (planLevel: 'basic' | 'pro' | 'pro_plus') => {
        showToast(`Subscribing to ${planLevel} is coming soon!`);
    };
    
    const handleBuyPAYG = (item: string, type: 'single' | 'pack', price: string) => {
        showToast(`Purchase ${item} (${type}) for ${price} - Coming Soon!`);
    }

    if (clusterPrefix === 'fan') {
        return (
            <div className="bg-brand-surface rounded-lg p-8 text-center max-w-2xl mx-auto font-urbanist">
                <SparklesIcon className="h-12 w-12 text-brand-orange mx-auto mb-4" />
                <h2 className="text-3xl font-black text-white mb-4 uppercase tracking-tight">Fan Accounts are Free!</h2>
                <p className="text-gray-300">As a Fan, you get full access to discover events, concerts, and gigs for free. We are currently working on exclusive Fan+ features like early-bird tickets and artist collectibles.</p>
                <div className="mt-8 p-4 bg-gray-800 rounded-xl border border-brand-orange/30 inline-block">
                    <p className="text-brand-orange font-bold tracking-widest text-sm uppercase">Stay Tuned for Fan Perks</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 font-urbanist pb-12">
            <div className="text-center">
                <SparklesIcon className="h-12 w-12 text-brand-orange mx-auto" />
                <h1 className="mt-4 text-4xl font-black text-white uppercase tracking-tighter">Pump Up The Volume</h1>
                
                <div className="mt-6 mb-8">
                    <CountdownTimer targetDate="2027-01-06T00:00:00" />
                    <p className="mt-2 text-[10px] text-brand-muted uppercase font-black tracking-[0.3em]">Until Paid Plans Begin</p>
                </div>

                <div className="flex justify-center mt-8">
                    <div className="bg-gray-800 p-1 rounded-full flex relative">
                        <button 
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-6 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${billingCycle === 'monthly' ? 'bg-gray-700 text-white shadow-lg' : 'text-brand-muted hover:text-white'}`}
                        >
                            Monthly
                        </button>
                        <button 
                            onClick={() => setBillingCycle('yearly')}
                            className={`px-6 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all flex items-center ${billingCycle === 'yearly' ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:text-white'}`}
                        >
                            Yearly
                            <span className="ml-2 text-[10px] bg-white text-brand-orange px-1.5 py-0.5 rounded-full">-17%</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                <PricingCard 
                    planName="Basic" 
                    tier={`${clusterPrefix}_basic`} 
                    price="Free" 
                    features={features.Basic} 
                    onSubscribe={() => handleSubscribe('basic')} 
                    currentTier={currentUser?.subscriptionTier} 
                />
                <PricingCard 
                    planName="Pro" 
                    tier={`${clusterPrefix}_pro`} 
                    price={billingCycle === 'monthly' ? 9.99 : 99} 
                    period={billingCycle === 'monthly' ? '/mo' : '/yr'}
                    features={features.Pro} 
                    isPopular 
                    onSubscribe={() => handleSubscribe('pro')} 
                    currentTier={currentUser?.subscriptionTier} 
                    savingsText={billingCycle === 'yearly' ? "Save €20.88/yr" : undefined}
                />
                <PricingCard 
                    planName="Pro+" 
                    tier={`${clusterPrefix}_pro_plus`} 
                    price={billingCycle === 'monthly' ? 12.99 : 129} 
                    period={billingCycle === 'monthly' ? '/mo' : '/yr'}
                    features={features.ProPlus} 
                    onSubscribe={() => handleSubscribe('pro_plus')} 
                    currentTier={currentUser?.subscriptionTier}
                    savingsText={billingCycle === 'yearly' ? "Save €26.88/yr" : undefined}
                />
            </div>
            
            {/* Pay As You Go Section */}
            <div className="max-w-4xl mx-auto pt-12 border-t border-gray-800">
                <h3 className="text-2xl font-black text-white text-center mb-4 uppercase tracking-tighter">Pay as you go</h3>
                <p className="text-center text-brand-muted mb-10 max-w-xl mx-auto font-medium">
                    Hit your monthly limit? Unlock individual features or grab a discounted pack to keep the volume up.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <PayAsYouGoCard 
                        icon={<BriefcaseIcon className="h-8 w-8 text-green-400" />}
                        title="Apply"
                        description="Apply to one extra opportunity beyond your monthly limit."
                        singlePrice="€0.59"
                        packPrice="€2.49"
                        onBuy={(type, price) => handleBuyPAYG('Apply', type, price)}
                    />
                    <PayAsYouGoCard 
                        icon={<ChatBubbleIcon className="h-8 w-8 text-blue-400" />}
                        title="Unlock Chat"
                        description="Start more conversations beyond your monthly limit."
                        singlePrice="€0.49"
                        packPrice="€2.00"
                        onBuy={(type, price) => handleBuyPAYG('Unlock Chat', type, price)}
                    />
                    <PayAsYouGoCard 
                        icon={<AmplifierIcon className="h-8 w-8 text-brand-orange" />}
                        title="Post Opp"
                        description="Post a single Job, Gig, or Event beyond your monthly limit."
                        singlePrice="€0.99"
                        packPrice="€3.99"
                        onBuy={(type, price) => handleBuyPAYG('Post Opportunity', type, price)}
                    />
                    <PayAsYouGoCard 
                        icon={<PhotoIcon className="h-8 w-8 text-purple-400" />}
                        title="Lineup & Flyer"
                        description="Create one lineup and generate a professional promotional flyer."
                        singlePrice="€1.49"
                        packPrice="€5.99"
                        onBuy={(type, price) => handleBuyPAYG('Lineup & Flyer', type, price)}
                    />
                </div>
            </div>
            
             <div className="text-center max-w-lg mx-auto pt-12">
                <h3 className="text-xs font-black text-brand-muted uppercase tracking-[0.2em] mb-4">Secure Payments via</h3>
                <div className="flex items-center justify-center space-x-6">
                   <CreditCardIcon className="h-8 w-8 text-gray-500" />
                   <PayPalIcon className="h-8 w-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all cursor-help" />
                   <GooglePayIcon className="h-10 w-10 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all cursor-help" />
                   <ApplePayIcon className="h-10 w-10 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all cursor-help" />
                </div>
            </div>
        </div>
    );
};

const CountdownTimer: React.FC<{ targetDate: string }> = ({ targetDate }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    
    useEffect(() => {
        const i = setInterval(() => {
            const diff = +new Date(targetDate) - +new Date();
            if (diff > 0) {
                setTimeLeft({ 
                    days: Math.floor(diff / (1000 * 60 * 60 * 24)), 
                    hours: Math.floor((diff / (1000 * 60 * 60)) % 24), 
                    minutes: Math.floor((diff / 1000 / 60) % 60), 
                    seconds: Math.floor((diff / 1000) % 60) 
                });
            }
        }, 1000);
        return () => clearInterval(i);
    }, [targetDate]);

    return (
        <div className="flex justify-center gap-4 text-white">
            <TimeBox value={timeLeft.days} label="DAYS" />
            <TimeBox value={timeLeft.hours} label="HOURS" />
            <TimeBox value={timeLeft.minutes} label="MINS" />
            <TimeBox value={timeLeft.seconds} label="SECS" />
        </div>
    );
};

const TimeBox: React.FC<{ value: number, label: string }> = ({ value, label }) => (
    <div className="flex flex-col items-center">
        <div className="bg-gray-800/80 border border-gray-700 rounded-xl p-3 w-16 md:w-20 text-center shadow-2xl">
            <span className="text-2xl md:text-3xl font-mono font-bold text-brand-orange">{value}</span>
        </div>
        <span className="text-[10px] text-gray-500 mt-1 font-black tracking-widest">{label}</span>
    </div>
);

interface PricingCardProps {
    planName: string;
    tier: string;
    price: number | 'Free';
    period?: string;
    features: (string | React.ReactNode)[];
    isPopular?: boolean;
    onSubscribe: () => void;
    currentTier?: string;
    savingsText?: string;
}

const PricingCard: React.FC<PricingCardProps> = ({ planName, tier, price, period = '/mo', features, isPopular, onSubscribe, currentTier, savingsText }) => {
    const isCurrentPlan = currentTier === tier || (planName === 'Basic' && (currentTier === 'basic' || currentTier?.endsWith('_basic')));

    return (
        <div className={`bg-brand-surface rounded-2xl p-8 flex flex-col border-2 relative transition-all duration-300 ${isPopular ? 'border-brand-orange shadow-2xl shadow-brand-orange/10 scale-[1.02] z-10' : 'border-gray-800 hover:border-gray-700'}`}>
            {isPopular && (
                <span className="bg-brand-orange text-white text-[10px] font-black tracking-widest px-4 py-1 rounded-full absolute -top-3 left-1/2 -translate-x-1/2 uppercase">Most Popular</span>
            )}
            {savingsText && (
                <span className="absolute top-4 right-4 text-[9px] font-black text-green-400 bg-green-500/10 px-2 py-1 rounded border border-green-500/20 uppercase tracking-widest">{savingsText}</span>
            )}
            <h2 className="text-2xl font-black text-white text-center mt-2 uppercase tracking-tight">{planName}</h2>
            <div className="text-center my-6 h-12 flex items-center justify-center">
                {price === 'Free' ? (
                    <span className="text-4xl font-black text-white">Free</span>
                ) : (
                    <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-black text-white">€{price}</span>
                        <span className="text-brand-muted ml-1 text-sm font-bold">{period}</span>
                    </div>
                )}
            </div>
            <ul className="space-y-4 flex-grow mb-8">
                {features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="ml-3 text-sm text-gray-300 font-medium">{feature}</span>
                    </li>
                ))}
            </ul>
            <button
                onClick={onSubscribe}
                disabled={isCurrentPlan}
                className={`w-full font-black py-4 rounded-full transition-all text-xs uppercase tracking-[0.2em] shadow-lg ${
                    isCurrentPlan 
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'bg-brand-orange text-white hover:bg-orange-600 shadow-brand-orange/20 active:scale-95'
                }`}
            >
                {isCurrentPlan ? 'Active Plan' : 'Subscribe Now'}
            </button>
        </div>
    );
};

const PayAsYouGoCard: React.FC<{ 
    icon: React.ReactNode, 
    title: string, 
    description: string, 
    singlePrice: string, 
    packPrice: string,
    onBuy: (type: 'single' | 'pack', price: string) => void 
}> = ({ icon, title, description, singlePrice, packPrice, onBuy }) => (
    <div className="bg-brand-surface rounded-2xl p-6 border border-gray-800 hover:border-gray-600 transition-all duration-300 flex flex-col items-center text-center group shadow-lg">
        <div className="bg-gray-800/60 p-4 rounded-2xl mb-4 border border-gray-700 group-hover:border-brand-orange/30 transition-colors">
            {icon}
        </div>
        <h3 className="text-xl font-black text-white mb-2 uppercase tracking-tight">{title}</h3>
        <p className="text-xs text-brand-muted mb-6 flex-grow leading-relaxed font-medium">{description}</p>
        <div className="w-full mt-auto space-y-3">
            <button 
                onClick={() => onBuy('single', singlePrice)} 
                className="w-full flex justify-between items-center bg-gray-800 hover:bg-gray-700 text-white font-black py-3 px-5 rounded-xl transition-all border border-gray-700 text-[10px] uppercase tracking-widest"
            >
                <span>Single</span>
                <span className="text-brand-orange">{singlePrice}</span>
            </button>
            <button 
                onClick={() => onBuy('pack', packPrice)} 
                className="w-full flex justify-between items-center bg-brand-orange hover:bg-orange-600 text-white font-black py-3 px-5 rounded-xl transition-all shadow-lg shadow-brand-orange/10 text-[10px] uppercase tracking-widest"
            >
                <span>Pack of 5</span>
                <span>{packPrice}</span>
            </button>
        </div>
    </div>
);

export default PumpUpTheVolumePage;